import {
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ChatBubbleComponent,
  ChatBubbleMessageKind,
} from '../../molecules/chat-bubble/chat-bubble.component';
import { MessageButtonClickEvent, MessageStateType } from '../../../interfaces';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Message, Participant } from '@clientwire/api';
import { FaIconComponent, MessageComponent } from '@akostm/orbit-ui';
import { InViewportAction, InViewportModule } from 'ng-in-viewport';
import { faCircleChevronDown, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { debounceTime, Subject, tap, throttleTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { logger } from '../../../utils';
import { FindByPipe } from '../../../pipes';
import { ErrorState, ParticipantWithSourceId } from '../../../stores';
import { TranslatePipe } from '@ngx-translate/core';
import { WindowService } from '../../../services';

@Component({
  selector: 'shared-chat-messages',
  standalone: true,
  imports: [
    ChatBubbleComponent,
    InfiniteScrollDirective,
    ScrollingModule,
    FaIconComponent,
    InViewportModule,
    FindByPipe,
    MessageComponent,
    TranslatePipe,
  ],
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChatMessagesComponent implements OnChanges {
  //#region Inputs
  readonly conversationParticipants = input<Participant[]>();
  readonly messages = input.required<Message[] | null>();
  readonly messageIdsWithError = input<string[]>();
  readonly messagePrependLoading = input<boolean>(false);
  readonly messageAppendLoading = input<boolean>(false);
  readonly otherTypingParticipants = input<ParticipantWithSourceId[]>();
  readonly participant = input<Participant>();
  readonly unreadMessages = input<number>(0);
  readonly errors = input<ErrorState[]>();
  readonly windowService = input.required<WindowService>();
  readonly conversationAppType = input.required<'main' | 'embed' | 'client'>();
  readonly sharedUserDisplayName = input<string | undefined | null>();
  //#endregion

  //#region Outputs
  readonly assetClick = output<string>();
  readonly buttonClick = output<MessageButtonClickEvent>();
  readonly messageRead = output<Message>();
  readonly scrolled = output<void>();
  readonly scrolledUp = output<void>();
  readonly scrolledToBottom = output<boolean>();
  readonly resendMessage = output<string>();
  readonly refreshMessages = output<void>();
  //#endregion

  //#region Computed
  readonly lastSeenMessageSequence = computed(() => {
    const otherParticipants = this.conversationParticipants()?.filter(
      (participant) => participant.id !== this.participant()?.id
    );
    const lastSeenMessageSequence = otherParticipants?.reduce((max, participant) => {
      return Math.max(max, participant.lastMessageSequenceSeen ?? 0);
    }, 0);
    return lastSeenMessageSequence;
  });
  //#endregion

  //#region ViewChild
  readonly chatWrapper = viewChild('chatWrapper', { read: ElementRef });
  //#endregion

  //#region Variables
  readonly showUnreadMessages = signal<boolean>(false);
  readonly arrowIcon = faCircleChevronDown;
  readonly spinnerIcon = faSpinnerThird;
  readonly refreshIcon = faRefresh;
  readonly #listScrolled = new Subject<Event>();
  readonly messageStateTypeEnum = MessageStateType;
  readonly chatBubbleKind = ChatBubbleMessageKind;
  readonly #visibleMessages = new Map<Message['sequence'], Message>();
  //#endregion

  constructor() {
    this.#listScrolled
      .pipe(
        throttleTime(500),
        tap((data) => {
          return data;
        }),
        debounceTime(550),
        takeUntilDestroyed()
      )
      .subscribe({
        next: () => {
          this.#checkNewReadMessages();
        },
        error: (err) => {
          logger.error('Error: %s', err);
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['messages'] &&
      changes['messages'].currentValue &&
      changes['messages'].previousValue
    ) {
      const newMessages = changes['messages'].currentValue;
      if (newMessages[0]?.id !== changes['messages'].previousValue[0]?.id) {
        if (newMessages[0]?.participantId === this.participant()?.id) {
          setTimeout(() => {
            this.scrollToBottom();
          }, 0);
        } else {
          this.#checkScrollPosition();
        }
      }
    }
  }

  scrollToBottom(): void {
    const chatWrapper = this.chatWrapper();
    if (chatWrapper && chatWrapper.nativeElement) {
      chatWrapper.nativeElement.scroll({
        top: 0,
        behavior: 'smooth',
      });
      this.showUnreadMessages.set(false);
      this.scrolledToBottom.emit(true);
    }
  }

  lastMessageVisible({ visible }: { target: Element; visible: boolean }): void {
    if (visible) {
      this.showUnreadMessages.set(false);
      this.scrolledToBottom.emit(true);
    } else {
      this.scrolledToBottom.emit(false);
    }
  }

  onScroll(event: Event): void {
    this.#listScrolled.next(event);
  }

  toggleMessageVisibility(event: InViewportAction, message: Message): void {
    if (event.visible) {
      this.#visibleMessages.set(message.sequence, message);
    } else {
      this.#visibleMessages.delete(message.sequence);
    }
  }

  #checkNewReadMessages(): void {
    // TODO: Inspect if sequence is missing how this sort behaves
    const highestVisibleMessageSequence = [...this.#visibleMessages.entries()].sort(
      (a, b) => (b[0] ?? 0) - (a[0] ?? 0)
    )[0];

    // TODO: Check if there are no messages and the first message is a message withour sequence
    if (
      highestVisibleMessageSequence &&
      highestVisibleMessageSequence[0] &&
      highestVisibleMessageSequence[0] > (this.participant()?.lastMessageSequenceSeen ?? 0)
    ) {
      this.messageRead.emit(highestVisibleMessageSequence[1]);
    }
  }

  #checkScrollPosition(): void {
    const chatWrapper = this.chatWrapper();
    if (chatWrapper && chatWrapper.nativeElement) {
      if (chatWrapper.nativeElement.scrollTop < 0) {
        this.showUnreadMessages.set(true);
        this.scrolledToBottom.emit(false);
      }
    }
  }
}
